import { guardArray } from "../array";
import { moment } from "../date";
import type { Tables } from "../types";

export type CapacityUtilizationTicketBase = Tables<"tickets"> & {
	_project: Array<Pick<Tables<"projects">, "id" | "name">>;
};

/**
 * This function maps capacity utilization tickets to include the project and date range.
 * @param tickets
 * @returns
 */
export function mapCapacityUtilizationTickets(tickets: CapacityUtilizationTicketBase[]) {
	return tickets.map((ticket) => {
		ticket._project = guardArray(ticket._project);
		return {
			range: moment().rangeDatesDb(ticket.startDate, ticket.endDate),
			ticket,
		};
	});
}

export type CapacityUtilizationTickets = ReturnType<typeof mapCapacityUtilizationTickets>;

/**
 * This function creates an empty capacity utilization object.
 * It can be used to initialize a capacity utilization record.
 * @returns
 */
export function createCapacityUtilization(
	year: number,
	tickets: CapacityUtilizationTickets,
): Record<string, number> {
	const flatRangeDates = tickets.flatMap((t) => t.range);
	return moment()
		.year(year)
		.yearDatesDb()
		.reduce(
			(acc, date) => {
				acc[date] = flatRangeDates.filter((str) => str === date).length;
				return acc;
			},
			{} as Record<string, number>,
		);
}

/**
 * This function calculates the maximum capacity utilization from a given record.
 * @param capacityUtilization
 * @returns
 */
export function getMaxUtilization(capacityUtilization: Record<string, number>): number {
	return Math.max(...Object.values(capacityUtilization));
}

/**
 * This function creates capacity utilization statistics for a given year.
 * @param year
 * @param _tickets
 * @returns
 */
export function createCapacityUtilizationStatistics(
	year: number,
	_tickets: CapacityUtilizationTicketBase[],
) {
	preprocessCapacityUtilizationTickets(_tickets);
	const tickets = mapCapacityUtilizationTickets(_tickets);
	const capacityUtilization = createCapacityUtilization(year, tickets);
	const maxUtilization = getMaxUtilization(capacityUtilization);
	return { capacityUtilization, maxUtilization, tickets };
}

function preprocessCapacityUtilizationTickets(tickets: CapacityUtilizationTicketBase[]) {
	tickets.sort((a, b) => {
		const startDateA = a.startDate || "";
		const startDateB = b.startDate || "";
		return new Date(startDateA).getTime() - new Date(startDateB).getTime();
	});
}

/**
 * This function retrieves the capacity utilization statistics base by an already created statistics object.
 */
export function getCapacityUtilizationStatisticsBase(
	statistics: ReturnType<typeof createCapacityUtilizationStatistics>,
): CapacityUtilizationTicketBase[] {
	return statistics.tickets.map((t) => t.ticket);
}
